import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import OwnerDashboard from "@/pages/OwnerDashboard";
import ShopDashboard from "@/pages/ShopDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import Settings from "@/pages/Settings";
import PendingApproval from "@/pages/PendingApproval";
import ShopApplication from "@/pages/ShopApplication";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { TrialEndingToast } from "@/components/TrialEndingToast";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useFcmPush } from "@/hooks/useFcmPush";
import PostIssue from "@/pages/PostIssue";
import IssueDetail from "@/pages/IssueDetail";
import ShopsList from "@/pages/ShopsList";
import Messages from "@/pages/Messages";
import ShopProfile from "@/pages/ShopProfile";
import Upgrade from "@/pages/Upgrade";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import MapView from "@/pages/MapView";
import { DeepLinkListener } from "@/components/DeepLinkListener";
import { initNativeShell } from "@/lib/native";
import { initIap } from "@/lib/iap";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-500 label-eyebrow">LOADING...</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role) return <Navigate to="/onboarding" replace />;
  // Shops with non-approved status are routed to the pending screen
  if (user.role === "shop" && user.status && user.status !== "approved") {
    const allowed = ["/pending", "/settings", "/shop/apply"];
    if (!allowed.includes(window.location.pathname)) {
      return <Navigate to="/pending" replace />;
    }
  }
  if (role && user.role !== role) {
    const dest = user.role === "admin" ? "/admin" : user.role === "shop" ? "/shop" : "/owner";
    return <Navigate to={dest} replace />;
  }
  return children;
}

function RootRouter() {
  const location = useLocation();
  // Handle OAuth callback via URL fragment (session_id=...)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/owner" element={<Protected role="owner"><OwnerDashboard /></Protected>} />
      <Route path="/post" element={<Protected role="owner"><PostIssue /></Protected>} />
      <Route path="/shop" element={<Protected role="shop"><ShopDashboard /></Protected>} />
      <Route path="/browse" element={<Protected role="shop"><ShopDashboard /></Protected>} />
      <Route path="/shop-profile" element={<Protected role="shop"><ShopProfile /></Protected>} />
      <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
      <Route path="/pending" element={<Protected><PendingApproval /></Protected>} />
      <Route path="/shop/apply" element={<Protected><ShopApplication /></Protected>} />
      <Route path="/shops" element={<Protected><ShopsList /></Protected>} />
      <Route path="/issue/:id" element={<Protected><IssueDetail /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/upgrade" element={<Protected role="shop"><Upgrade /></Protected>} />
      <Route path="/map" element={<Protected><MapView /></Protected>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    initNativeShell();
    initIap();
  }, []);
  return (
    <div className="App">
      <Toaster theme="dark" position="top-right" toastOptions={{ className: "!rounded-none !bg-[#121214] !border-white/10 !text-white" }} />
      <AuthProvider>
        <BrowserRouter>
          <DeepLinkListener />
          <ImpersonationBanner />
          <TrialEndingToast />
          <InstallPrompt />
          <FcmBootstrap />
          <RootRouter />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

// Tiny wrapper so the hook can sit inside <AuthProvider>.
function FcmBootstrap() {
  useFcmPush();
  return null;
}

export default App;
